import { useEffect, useRef, useState } from 'react';
import '@tylertech/forge/dialog';
import '@tylertech/forge/button';
import '@tylertech/forge/text-field';
import '@tylertech/forge/select';
import type { CreateApiKeyInput, ApiKeyScope } from '../../types/apiKeys';
import './CreateApiKeyDialog.css';

export interface CreateApiKeyDialogProps {
  open: boolean;
  isSubmitting: boolean;
  onSubmit: (input: CreateApiKeyInput) => void;
  onCancel: () => void;
}

const DialogElement = 'forge-dialog' as React.ElementType;
const ForgeButton = 'forge-button' as React.ElementType;

const SCOPE_OPTIONS: Array<{ label: string; value: ApiKeyScope; hint: string }> = [
  { label: 'Read only (*.read)', value: '*.read', hint: 'GraphQL reads, MCP read tools' },
  {
    label: 'Service write (service.write)',
    value: 'service.write',
    hint: 'Application pillar: services, teams, dependencies, tags',
  },
  {
    label: 'Technology write (technology.write)',
    value: 'technology.write',
    hint: 'Technology pillar: technologies, product families, suppliers',
  },
  {
    label: 'Information write (information.write)',
    value: 'information.write',
    hint: 'Information pillar: data domains, data objects',
  },
  {
    label: 'Business write (business.write)',
    value: 'business.write',
    hint: 'Business pillar: capabilities, maturity frameworks/assessments',
  },
  {
    label: 'Ingestion write (ingestion.write)',
    value: 'ingestion.write',
    hint: 'POST /api/v1/integrations/ingest',
  },
  {
    label: 'Service all-teams read (service.read.allTeams)',
    value: 'service.read.allTeams',
    hint: 'tyl_eam_entity_search across every team’s Services, without teamId/milestoneId',
  },
  { label: 'Admin — full user permissions (admin)', value: 'admin', hint: 'Satisfies every scope' },
];

const MAX_EXPIRY_DATE = (): string => {
  const d = new Date();
  d.setDate(d.getDate() + 365);
  return d.toISOString().split('T')[0];
};

const DEFAULT_EXPIRY = (): string => {
  const d = new Date();
  d.setDate(d.getDate() + 90);
  return d.toISOString().split('T')[0];
};

export function CreateApiKeyDialog({
  open,
  isSubmitting,
  onSubmit,
  onCancel,
}: CreateApiKeyDialogProps): JSX.Element | null {
  const dialogRef = useRef<HTMLElement & { open: boolean }>(null);
  const [keyName, setKeyName] = useState('');
  const [scopes, setScopes] = useState<ApiKeyScope[]>(['*.read']);
  const [expiresAt, setExpiresAt] = useState(DEFAULT_EXPIRY());
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.open = open;
    }
  }, [open]);

  const resetForm = (): void => {
    setKeyName('');
    setScopes(['*.read']);
    setExpiresAt(DEFAULT_EXPIRY());
    setNameError('');
  };

  const handleScopeToggle = (scope: ApiKeyScope): void => {
    setScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  const handleSubmit = (): void => {
    if (!keyName.trim()) {
      setNameError('Key name is required');
      return;
    }
    onSubmit({ keyName: keyName.trim(), scopes, expiresAt });
    resetForm();
  };

  const handleCancel = (): void => {
    resetForm();
    onCancel();
  };

  if (!open) return null;

  const titleId = 'create-key-dialog-title';

  return (
    <DialogElement
      ref={dialogRef}
      className="create-key-dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="dialog-content dialog-content--flow">
        <div className="create-key-dialog-header">
          <h2 id={titleId} className="forge-typography--headline5">
            New API Key
          </h2>
        </div>

        <div className="create-key-dialog-body">
          <div className="create-key-form-field">
            <label htmlFor="key-name" className="forge-typography--label1">
              Key name <span aria-hidden="true">*</span>
            </label>
            <input
              id="key-name"
              type="text"
              className="forge-text-field__input"
              value={keyName}
              onChange={(e) => {
                setKeyName(e.target.value);
                setNameError('');
              }}
              placeholder="e.g. github-actions-prod"
              aria-required="true"
              aria-describedby={nameError ? 'key-name-error' : undefined}
              aria-invalid={!!nameError}
              data-testid="key-name-input"
            />
            {nameError && (
              <span id="key-name-error" className="create-key-field-error" role="alert">
                {nameError}
              </span>
            )}
          </div>

          <fieldset className="create-key-form-field create-key-scopes-fieldset">
            <legend className="forge-typography--label1">Scopes</legend>
            {SCOPE_OPTIONS.map(({ label, value, hint }) => (
              <label key={value} className="create-key-scope-option">
                <input
                  type="checkbox"
                  checked={scopes.includes(value)}
                  onChange={() => handleScopeToggle(value)}
                  data-testid={`scope-${value}`}
                />
                <span className="create-key-scope-label">
                  {label}
                  <span className="create-key-scope-hint">{hint}</span>
                </span>
              </label>
            ))}
          </fieldset>

          <div className="create-key-form-field">
            <label htmlFor="key-expiry" className="forge-typography--label1">
              Expiration date <span aria-hidden="true">*</span>
            </label>
            <input
              id="key-expiry"
              type="date"
              className="forge-text-field__input"
              value={expiresAt}
              max={MAX_EXPIRY_DATE()}
              onChange={(e) => setExpiresAt(e.target.value)}
              aria-required="true"
              data-testid="key-expiry-input"
            />
          </div>
        </div>

        <div className="dialog-actions dialog-actions--borderless">
          <ForgeButton
            variant="text"
            onClick={handleCancel}
            disabled={isSubmitting || undefined}
            type="button"
          >
            Cancel
          </ForgeButton>
          <ForgeButton
            variant="raised"
            onClick={handleSubmit}
            disabled={isSubmitting || undefined}
            type="button"
            data-testid="submit-button"
          >
            {isSubmitting ? 'Creating...' : 'Create key'}
          </ForgeButton>
        </div>
      </div>
    </DialogElement>
  );
}
